/*
 * Function: Frontend controller for the Free Perfume / Gift Claim flow. It manages the multi-step UI, local validation, the Supabase-only verify and submit requests, proof-file processing, and success or retry state.
 * Related files: sections/customer-free-perfume-claim.liquid, assets/customer-free-perfume-claim.css, templates/page.customer-free-perfume-claim.json, C:/Users/tliu7/supabase-free-gift/supabase/functions/claim-verify/index.ts, C:/Users/tliu7/supabase-free-gift/supabase/functions/claim-submit/index.ts.
 */
(function () {
  var ROOT_SELECTOR = "[data-customer-free-perfume-claim]";
  var MAX_PROOF_DATA_URL_LENGTH = 650000;
  var SUBMISSION_TIMEOUT_MS = 90000;
  var PROOF_PROCESSING_TIMEOUT_MS = 20000;
  var CLAIM_DRAFT_TTL_MS = 72 * 60 * 60 * 1000;
  var DEFAULT_ADDRESS_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };
  var GOOGLE_MAPS_LOAD_TIMEOUT_MS = 20000;
  var pendingSubmissionState = null;
  var googleMapsLoader = null;
  var googleMapsScriptObserver = null;
  var allowedGoogleMapsScriptSrc = "";
  var UNITED_STATES_REGION_OPTIONS = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia",
    "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
    "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
    "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
    "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
    "District of Columbia"
  ];
  var CLAIM_DRAFT_FIELD_NAMES = [
    "code",
    "ref",
    "passcodeStatus",
    "name",
    "phone",
    "email",
    "streetAddress",
    "apartment",
    "zipCode",
    "city",
    "state",
    "country",
    "addressVerified",
    "addressFormatted",
    "addressPlaceId",
    "addressLatitude",
    "addressLongitude",
    "platform",
    "orderNote",
    "selectedGift",
    "selectedGiftVariantId"
  ];

  function parseConfig(root) {
    var configNode = root.querySelector("[data-claim-config]");
    var parsedConfig;

    if (!configNode) {
      return {};
    }

    try {
      parsedConfig = JSON.parse(configNode.textContent || "{}");
      parsedConfig.claimVerifyUrl = normalize(parsedConfig.claimVerifyUrl);
      parsedConfig.claimSubmitUrl = normalize(parsedConfig.claimSubmitUrl || parsedConfig.claimVerifyUrl);
      parsedConfig.googleMapsApiKey = normalize(parsedConfig.googleMapsApiKey);
      return parsedConfig;
    } catch (error) {
      return {};
    }
  }

  function normalize(value) {
    return (value || "").trim();
  }

  function isGoogleMapsApiScript(node) {
    return Boolean(node && node.tagName === "SCRIPT" && normalize(node.src).indexOf("maps.googleapis.com/maps/api/js") !== -1);
  }

  function rememberAllowedGoogleMapsScript(script) {
    if (!isGoogleMapsApiScript(script) || allowedGoogleMapsScriptSrc) {
      return;
    }

    allowedGoogleMapsScriptSrc = normalize(script.src);
  }

  function removeDuplicateGoogleMapsScript(script) {
    if (!isGoogleMapsApiScript(script)) {
      return;
    }

    if (!allowedGoogleMapsScriptSrc) {
      rememberAllowedGoogleMapsScript(script);
      return;
    }

    if (normalize(script.src) === allowedGoogleMapsScriptSrc) {
      return;
    }

    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  }

  function installGoogleMapsDuplicateBlocker() {
    if (googleMapsScriptObserver || typeof MutationObserver !== "function") {
      return;
    }

    googleMapsScriptObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
          if (isGoogleMapsApiScript(node)) {
            removeDuplicateGoogleMapsScript(node);
          }
        });
      });
    });

    googleMapsScriptObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function findExistingGoogleMapsScript() {
    return document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  }

  function waitForGoogleMapsAvailability(timeoutMs) {
    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();

      function finishWithResult() {
        if (window.google && window.google.maps) {
          resolve(window.google.maps);
          return true;
        }

        if (Date.now() - startedAt >= (timeoutMs || GOOGLE_MAPS_LOAD_TIMEOUT_MS)) {
          reject(new Error("Google Maps loaded too slowly or was blocked on this page."));
          return true;
        }

        return false;
      }

      function poll() {
        if (finishWithResult()) {
          return;
        }

        window.setTimeout(poll, 100);
      }

      poll();
    });
  }

  function buildReferenceToken() {
    return "PASS-" + Date.now() + "-" + Math.floor(Math.random() * 1000);
  }

  function getFieldValue(form, name) {
    var field = form.elements[name];

    if (!field) {
      return "";
    }

    if (field instanceof RadioNodeList) {
      return field.value || "";
    }

    return field.value || "";
  }

  function triggerNativeChange(field) {
    if (!field) {
      return;
    }

    field.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function triggerNativeInput(field) {
    if (!field) {
      return;
    }

    field.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function setSelectOptions(select, values, placeholder) {
    var options = Array.isArray(values) ? values : [];

    if (!select) {
      return;
    }

    select.innerHTML = "";
    select.appendChild(new Option(placeholder || "Select an option", ""));
    options.forEach(function (value) {
      if (!normalize(value)) {
        return;
      }

      select.appendChild(new Option(value, value));
    });
  }

  function setSelectValueByCandidates(select, candidates) {
    var normalizedCandidates = (Array.isArray(candidates) ? candidates : []).map(normalize).filter(Boolean);
    var match = "";

    if (!select || !normalizedCandidates.length) {
      return false;
    }

    Array.prototype.some.call(select.options, function (option) {
      var optionValue = normalize(option.value);
      var optionText = normalize(option.textContent || option.innerText || "");
      var matched = normalizedCandidates.some(function (candidate) {
        return optionValue === candidate || optionText === candidate;
      });

      if (matched) {
        match = option.value;
      }

      return matched;
    });

    if (!match) {
      return false;
    }

    select.value = match;
    return true;
  }

  function restrictCountryOptionsToUnitedStates(select) {
    var usOption;

    if (!select) {
      return;
    }

    usOption = Array.prototype.find.call(select.options || [], function (option) {
      var value = normalize(option.value);
      var label = normalize(option.textContent || option.innerText || "");
      return value === "United States" || value === "United States of America" || label === "United States" || label === "United States of America";
    });

    if (!usOption) {
      return;
    }

    select.innerHTML = "";
    select.appendChild(usOption.cloneNode(true));
    select.value = select.options[0].value;
  }

  function populateUnitedStatesRegions(select) {
    if (!select) {
      return;
    }

    setSelectOptions(select, UNITED_STATES_REGION_OPTIONS, "Select a state");
    select.disabled = false;
  }

  function loadGoogleMapsApi(apiKey) {
    return new Promise(function (resolve, reject) {
      var existingScript;
      var script;
      var callbackName;

      if (window.google && window.google.maps) {
        resolve(window.google.maps);
        return;
      }

      if (!normalize(apiKey)) {
        reject(new Error("Add a Google Maps API key in this section before collecting verified shipping addresses."));
        return;
      }

      if (googleMapsLoader) {
        googleMapsLoader.then(resolve).catch(reject);
        return;
      }

      existingScript = findExistingGoogleMapsScript();

      if (existingScript) {
        rememberAllowedGoogleMapsScript(existingScript);
        installGoogleMapsDuplicateBlocker();
        googleMapsLoader = waitForGoogleMapsAvailability(GOOGLE_MAPS_LOAD_TIMEOUT_MS);
        googleMapsLoader.then(resolve).catch(reject);
        return;
      }

      callbackName = "__customerFreePerfumeClaimGoogleMapsReady";
      script = document.createElement("script");
      rememberAllowedGoogleMapsScript(script);
      installGoogleMapsDuplicateBlocker();

      googleMapsLoader = new Promise(function (loaderResolve, loaderReject) {
        window[callbackName] = function () {
          try {
            delete window[callbackName];
          } catch (error) {
            window[callbackName] = undefined;
          }

            allowedGoogleMapsScriptSrc = normalize(script.src);
          loaderResolve(window.google.maps);
        };

        script.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(apiKey) + "&libraries=places&callback=" + callbackName;
        script.async = true;
        script.defer = true;
        script.setAttribute("data-claim-google-maps", "true");
        script.onerror = function () {
          loaderReject(new Error("Google Maps could not be loaded. Check the API key and enabled APIs."));
        };
        document.head.appendChild(script);
      });

      googleMapsLoader.then(resolve).catch(reject);
    });
  }

  function getAddressComponent(addressComponents, type, useShortName) {
    var match = (addressComponents || []).find(function (component) {
      return component.types && component.types.indexOf(type) !== -1;
    });

    if (!match) {
      return "";
    }

    return useShortName ? String(match.short_name || "").trim() : String(match.long_name || "").trim();
  }

  function buildStreetAddressFromComponents(addressComponents) {
    var streetNumber = getAddressComponent(addressComponents, "street_number");
    var route = getAddressComponent(addressComponents, "route");
    var premise = getAddressComponent(addressComponents, "premise");
    var subpremise = getAddressComponent(addressComponents, "subpremise");
    var line1 = [streetNumber, route].filter(Boolean).join(" ");

    if (line1) {
      return line1;
    }

    return [premise, subpremise].filter(Boolean).join(" ");
  }

  function parseVerifiedAddress(place) {
    var addressComponents = place && place.address_components ? place.address_components : [];
    var cityCandidates = [
      getAddressComponent(addressComponents, "locality"),
      getAddressComponent(addressComponents, "postal_town"),
      getAddressComponent(addressComponents, "sublocality_level_1"),
      getAddressComponent(addressComponents, "administrative_area_level_3")
    ].filter(function (value, index, values) {
      return normalize(value) && values.indexOf(value) === index;
    });
    var geometry = place && place.geometry && place.geometry.location ? place.geometry.location : null;

    return {
      formattedAddress: normalize(place && place.formatted_address),
      streetAddress: buildStreetAddressFromComponents(addressComponents),
      city: cityCandidates[0] || "",
      cityCandidates: cityCandidates,
      state: getAddressComponent(addressComponents, "administrative_area_level_1"),
      stateCode: getAddressComponent(addressComponents, "administrative_area_level_1", true),
      country: getAddressComponent(addressComponents, "country"),
      countryCode: getAddressComponent(addressComponents, "country", true),
      zipCode: getAddressComponent(addressComponents, "postal_code"),
      placeId: normalize(place && place.place_id),
      latitude: geometry && typeof geometry.lat === "function" ? String(geometry.lat()) : "",
      longitude: geometry && typeof geometry.lng === "function" ? String(geometry.lng()) : ""
    };
  }

  function buildAddressSignature(fields) {
    return [
      getFieldValue(fields.form, "streetAddress"),
      getFieldValue(fields.form, "zipCode"),
      getFieldValue(fields.form, "city"),
      getFieldValue(fields.form, "state"),
      getFieldValue(fields.form, "country")
    ].map(normalize).join("|");
  }

  function syncProvinceFieldState(addressFields) {
    var hasSelectableOption;

    if (!addressFields.stateSelect) {
      return;
    }

    hasSelectableOption = Array.prototype.some.call(addressFields.stateSelect.options || [], function (option) {
      return Boolean(normalize(option.value));
    });

    addressFields.stateSelect.disabled = !hasSelectableOption;
    addressFields.stateSelect.required = hasSelectableOption;
  }

  function setAddressMapMessage(addressFields, message) {
    if (!addressFields || !addressFields.mapStatusNode) {
      return;
    }

    addressFields.mapStatusNode.textContent = message || "";
  }

  function setAddressMapState(addressFields, state, message) {
    if (!addressFields) {
      return;
    }

    if (addressFields.mapShell) {
      addressFields.mapShell.setAttribute("data-state", state || "idle");
    }

    setAddressMapMessage(addressFields, message);
  }

  function getVerifiedMapLocation(parsedAddress) {
    var latitude = Number(parsedAddress && parsedAddress.latitude);
    var longitude = Number(parsedAddress && parsedAddress.longitude);

    if (!isFinite(latitude) || !isFinite(longitude)) {
      return null;
    }

    return {
      lat: latitude,
      lng: longitude
    };
  }

  function buildMapEmbedUrl(parsedAddress, nextLocation) {
    var query;

    if (!nextLocation) {
      return "";
    }

    query = normalize(parsedAddress && parsedAddress.formattedAddress)
      || (nextLocation.lat + "," + nextLocation.lng);

    return "https://www.google.com/maps?q=" + encodeURIComponent(query) + "&z=15&output=embed";
  }

  function ensureAddressMap(addressFields, addressState) {
    if (!addressFields || !addressFields.mapCanvas || !addressState) {
      return;
    }

    addressState.map = true;
  }

  function refreshAddressMap(addressFields, addressState) {
    if (!addressFields || !addressFields.mapCanvas || !addressState || !addressState.map) {
      return;
    }
  }

  function resetAddressMap(addressFields, addressState, state, message) {
    if (!addressFields || !addressState) {
      return;
    }

    if (addressFields.mapCanvas) {
      addressFields.mapCanvas.innerHTML = "";
    }

    addressState.lastMapLocation = null;
    setAddressMapState(addressFields, state || "idle", message || "");
  }

  function updateAddressMap(addressFields, parsedAddress, addressState) {
    var nextLocation = getVerifiedMapLocation(parsedAddress);

    if (!nextLocation || !addressState) {
      resetAddressMap(addressFields, addressState, "idle", "");
      return;
    }

    ensureAddressMap(addressFields, addressState);

    if (!addressFields.mapCanvas) {
      return;
    }

    addressState.lastMapLocation = nextLocation;
    addressFields.mapCanvas.innerHTML = '<iframe title="Verified delivery address map preview" src="'
      + buildMapEmbedUrl(parsedAddress, nextLocation).replace(/"/g, '&quot;')
      + '" loading="lazy" referrerpolicy="no-referrer-when-downgrade" style="width:100%;height:100%;min-height:280px;border:0;border-radius:16px;"></iframe>';
    setAddressMapState(
      addressFields,
      "ready",
      parsedAddress.formattedAddress
        ? "Map updated to: " + parsedAddress.formattedAddress
        : "Map updated to your verified delivery address."
    );
  }

  function clearVerifiedAddress(addressFields, statusMessage, tone, preserveLocationChoices) {
    if (!addressFields) {
      return;
    }

    if (addressFields.verifiedField) {
      addressFields.verifiedField.value = "false";
    }

    if (addressFields.formattedField) {
      addressFields.formattedField.value = "";
    }

    if (addressFields.placeIdField) {
      addressFields.placeIdField.value = "";
    }

    if (addressFields.latitudeField) {
      addressFields.latitudeField.value = "";
    }

    if (addressFields.longitudeField) {
      addressFields.longitudeField.value = "";
    }

    if (!preserveLocationChoices && addressFields.citySelect) {
      setSelectOptions(addressFields.citySelect, [], "Select a verified city");
      addressFields.citySelect.disabled = true;
    }

    if (addressFields.streetInput) {
      addressFields.streetInput.setCustomValidity("");
    }

    if (addressFields.citySelect) {
      addressFields.citySelect.setCustomValidity("");
    }

    if (addressFields.addressState) {
      resetAddressMap(
        addressFields,
        addressFields.addressState,
        tone === "is-error" ? "error" : "idle",
        tone === "is-error"
          ? "We couldn't place the map yet because this address still needs a Google verification match."
          : "The map will refresh automatically once Google confirms the address."
      );
    }

    setMessage(addressFields.statusNode, statusMessage || "Address verification is required before continuing.", tone || "is-info");
  }

  function applyVerifiedAddress(addressFields, parsedAddress, addressState) {
    var selectedCity;
    var normalizedCurrentCountry;
    var normalizedNextCountry;

    if (!addressFields || !parsedAddress) {
      return;
    }

    if (normalize(parsedAddress.streetAddress) && addressFields.streetInput) {
      addressFields.streetInput.value = parsedAddress.streetAddress;
    }

    if (normalize(parsedAddress.zipCode) && addressFields.zipInput) {
      addressFields.zipInput.value = parsedAddress.zipCode;
    }

    if (addressFields.countrySelect && normalize(parsedAddress.country)) {
      normalizedCurrentCountry = normalize(addressFields.countrySelect.value);
      normalizedNextCountry = normalize(parsedAddress.country);

      setSelectValueByCandidates(addressFields.countrySelect, [parsedAddress.country, parsedAddress.countryCode]);

      if (normalizedCurrentCountry !== normalizedNextCountry) {
        triggerNativeChange(addressFields.countrySelect);
      }
    }

    addressState.isApplyingVerifiedAddress = true;

    window.setTimeout(function () {
      syncProvinceFieldState(addressFields);

      if (addressFields.stateSelect && normalize(parsedAddress.state)) {
        setSelectValueByCandidates(addressFields.stateSelect, [parsedAddress.state, parsedAddress.stateCode]);
      }

      selectedCity = parsedAddress.cityCandidates && parsedAddress.cityCandidates.length
        ? parsedAddress.cityCandidates
        : [parsedAddress.city];

      setSelectOptions(addressFields.citySelect, selectedCity, "Select a verified city");
      if (normalize(parsedAddress.city)) {
        addressFields.citySelect.value = parsedAddress.city;
      }
      addressFields.citySelect.disabled = false;

      if (addressFields.verifiedField) {
        addressFields.verifiedField.value = "true";
      }

      if (addressFields.formattedField) {
        addressFields.formattedField.value = parsedAddress.formattedAddress;
      }

      if (addressFields.placeIdField) {
        addressFields.placeIdField.value = parsedAddress.placeId;
      }

      if (addressFields.latitudeField) {
        addressFields.latitudeField.value = parsedAddress.latitude;
      }

      if (addressFields.longitudeField) {
        addressFields.longitudeField.value = parsedAddress.longitude;
      }

      updateAddressMap(addressFields, parsedAddress, addressState);

      if (addressFields.streetInput) {
        addressFields.streetInput.setCustomValidity("");
      }

      if (addressFields.citySelect) {
        addressFields.citySelect.setCustomValidity("");
      }

      addressState.lastVerifiedSignature = buildAddressSignature(addressFields);
      addressState.isApplyingVerifiedAddress = false;
      setMessage(addressFields.statusNode, "Address verified and locked to Google Maps. If you change it, we will re-check it automatically.", "is-success");
    }, 0);
  }

  function buildGeocodeQuery(addressFields) {
    return [
      addressFields.streetInput && addressFields.streetInput.value,
      addressFields.zipInput && addressFields.zipInput.value,
      addressFields.citySelect && addressFields.citySelect.value,
      addressFields.stateSelect && addressFields.stateSelect.value,
      addressFields.countrySelect && addressFields.countrySelect.value
    ].filter(function (value) {
      return normalize(value);
    }).join(", ");
  }

  function verifyAddressFromFields(addressFields, addressState) {
    var query;

    if (!addressState || !addressState.geocoder) {
      return;
    }

    query = buildGeocodeQuery(addressFields);

    if (normalize(query).length < 8 || !normalize(addressFields.streetInput && addressFields.streetInput.value) || !normalize(addressFields.zipInput && addressFields.zipInput.value)) {
      clearVerifiedAddress(addressFields, "Enter at least a street address and ZIP code so Google Maps can verify the destination.", "is-info", true);
      return;
    }

    setMessage(addressFields.statusNode, "Checking your shipping address with Google Maps...", "is-info");

    addressState.geocoder.geocode({ address: query }, function (results, status) {
      if (status !== "OK" || !results || !results[0]) {
        clearVerifiedAddress(addressFields, "We could not verify this address. Pick a Google suggestion or adjust the street and ZIP code.", "is-error", true);
        return;
      }

      applyVerifiedAddress(addressFields, parseVerifiedAddress(results[0]), addressState);
    });
  }

  function initAddressVerification(root, form, config) {
    var addressFields = {
      form: form,
      streetInput: root.querySelector("[data-claim-street-address]"),
      zipInput: root.querySelector("[data-claim-zip-code]"),
      citySelect: root.querySelector("[data-claim-city-select]"),
      stateSelect: root.querySelector("[data-claim-state-select]"),
      stateContainer: root.querySelector("[data-claim-state-container]"),
      countrySelect: root.querySelector("[data-claim-country-select]"),
      statusNode: root.querySelector("[data-claim-address-status]"),
      mapShell: root.querySelector("[data-claim-address-map-shell]"),
      mapCanvas: root.querySelector("[data-claim-address-map-canvas]"),
      mapStatusNode: root.querySelector("[data-claim-address-map-status]"),
      verifiedField: root.querySelector("[data-claim-address-verified]"),
      formattedField: root.querySelector("[data-claim-address-formatted]"),
      placeIdField: root.querySelector("[data-claim-address-place-id]"),
      latitudeField: root.querySelector("[data-claim-address-lat]"),
      longitudeField: root.querySelector("[data-claim-address-lng]")
    };
    var addressState = {
      geocoder: null,
      autocomplete: null,
      map: null,
      maps: null,
      marker: null,
      lastMapLocation: null,
      lastVerifiedSignature: "",
      verifyTimerId: null,
      isApplyingVerifiedAddress: false
    };

    function queueVerification() {
      var latestSignature;

      if (addressState.isApplyingVerifiedAddress) {
        return;
      }

      latestSignature = buildAddressSignature(addressFields);

      if (latestSignature === addressState.lastVerifiedSignature && getFieldValue(form, "addressVerified") === "true") {
        return;
      }

      clearVerifiedAddress(addressFields, "Address changed. Re-checking it against Google Maps...", "is-info", false);
      window.clearTimeout(addressState.verifyTimerId);
      addressState.verifyTimerId = window.setTimeout(function () {
        verifyAddressFromFields(addressFields, addressState);
      }, 450);
    }

    if (!addressFields.streetInput || !addressFields.zipInput || !addressFields.citySelect || !addressFields.stateSelect || !addressFields.countrySelect) {
      return null;
    }

    addressFields.addressState = addressState;

    setSelectOptions(addressFields.citySelect, [], "Select a verified city");
    addressFields.citySelect.disabled = true;
    restrictCountryOptionsToUnitedStates(addressFields.countrySelect);

    populateUnitedStatesRegions(addressFields.stateSelect);

    if (!addressFields.countrySelect.value) {
      setSelectValueByCandidates(addressFields.countrySelect, ["United States", "United States of America"]);
      triggerNativeChange(addressFields.countrySelect);
    }

    window.setTimeout(function () {
      syncProvinceFieldState(addressFields);
    }, 0);

    [addressFields.streetInput, addressFields.zipInput].forEach(function (field) {
      field.addEventListener("input", queueVerification);
      field.addEventListener("blur", queueVerification);
    });

    [addressFields.citySelect, addressFields.stateSelect, addressFields.countrySelect].forEach(function (field) {
      field.addEventListener("change", queueVerification);
    });

    addressFields.countrySelect.addEventListener("change", function () {
      window.setTimeout(function () {
        syncProvinceFieldState(addressFields);
      }, 0);
    });

    loadGoogleMapsApi(config.googleMapsApiKey)
      .then(function (maps) {
        var supportsPlacesAutocomplete = Boolean(maps && maps.places && typeof maps.places.Autocomplete === "function");

        addressState.maps = maps;
        addressState.geocoder = new maps.Geocoder();

        if (supportsPlacesAutocomplete) {
          addressState.autocomplete = new maps.places.Autocomplete(addressFields.streetInput, {
            fields: ["address_components", "formatted_address", "geometry", "place_id"],
            types: ["address"]
          });

          addressState.autocomplete.addListener("place_changed", function () {
            var place = addressState.autocomplete.getPlace();
            var parsedAddress = parseVerifiedAddress(place || {});

            if (!normalize(parsedAddress.streetAddress) || !normalize(parsedAddress.zipCode) || !normalize(parsedAddress.city) || !normalize(parsedAddress.state) || !normalize(parsedAddress.country)) {
              clearVerifiedAddress(addressFields, "Pick a more complete Google Maps address suggestion so city, state, country, and ZIP can be locked correctly.", "is-error", false);
              return;
            }

            applyVerifiedAddress(addressFields, parsedAddress, addressState);
          });

          setMessage(addressFields.statusNode, "", "");
        } else {
          setMessage(addressFields.statusNode, "Google address suggestions are unavailable on this page, but the map will still verify and update after you finish the address.", "is-info");
        }

        setAddressMapState(addressFields, "idle", "");
      })
      .catch(function (error) {
        clearVerifiedAddress(addressFields, error && error.message ? error.message : "Google Maps address verification is unavailable.", "is-error", false);
      });

    return addressFields;
  }

  function readFileAsDataUrl(file) {
    return new Promise(function (resolve, reject) {
      var reader;
      var timeoutId;

      function cleanup() {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      }

      if (!file) {
        resolve("");
        return;
      }

      reader = new FileReader();
      reader.onload = function () {
        cleanup();
        resolve(reader.result || "");
      };
      reader.onerror = function () {
        cleanup();
        reject(new Error("Could not read the selected file."));
      };

      timeoutId = window.setTimeout(function () {
        try {
          reader.abort();
        } catch (error) {}

        reject(new Error("Proof file processing timed out on this device. Please retry with a smaller screenshot."));
      }, PROOF_PROCESSING_TIMEOUT_MS);

      reader.readAsDataURL(file);
    });
  }

  function loadImageFile(file) {
    return new Promise(function (resolve, reject) {
      var objectUrl;
      var image = new Image();
      var timeoutId;

      function cleanup() {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }

        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }

      objectUrl = URL.createObjectURL(file);

      image.onload = function () {
        cleanup();
        resolve(image);
      };

      image.onerror = function () {
        cleanup();
        reject(new Error("Could not process the selected screenshot."));
      };

      timeoutId = window.setTimeout(function () {
        cleanup();
        reject(new Error("Screenshot processing timed out on this device. Please retry with a smaller screenshot."));
      }, PROOF_PROCESSING_TIMEOUT_MS);

      image.src = objectUrl;
    });
  }

  function buildCompressedImageDataUrl(image, mimeType) {
    var canvas = document.createElement("canvas");
    var context = canvas.getContext("2d");
    var maxDimension = 1280;
    var quality = 0.74;
    var width = image.naturalWidth || image.width;
    var height = image.naturalHeight || image.height;
    var longestEdge = Math.max(width, height);
    var scale = longestEdge > maxDimension ? maxDimension / longestEdge : 1;
    var exportType = /^image\/(png|webp|jpeg|jpg)$/i.test(mimeType) ? "image/jpeg" : "image/jpeg";
    var dataUrl = "";

    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    do {
      dataUrl = canvas.toDataURL(exportType, quality);

      if (dataUrl.length <= MAX_PROOF_DATA_URL_LENGTH || (canvas.width <= 720 && canvas.height <= 720 && quality <= 0.5)) {
        break;
      }

      canvas.width = Math.max(1, Math.round(canvas.width * 0.8));
      canvas.height = Math.max(1, Math.round(canvas.height * 0.8));
      context = canvas.getContext("2d");
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      quality = Math.max(0.5, quality - 0.1);
    } while (true);

    if (dataUrl.length > MAX_PROOF_DATA_URL_LENGTH) {
      throw new Error("Please upload a smaller screenshot. Large image payloads can fail during verification.");
    }

    return dataUrl;
  }

  function buildProofPayload(file) {
    if (!file) {
      return Promise.resolve({
        dataUrl: "",
        fileName: "",
        mimeType: ""
      });
    }

    if (/^image\//i.test(file.type || "")) {
      return loadImageFile(file).then(function (image) {
        return {
          dataUrl: buildCompressedImageDataUrl(image, file.type),
          fileName: (file.name || "proof-upload").replace(/\.[^.]+$/, ".jpg"),
          mimeType: "image/jpeg"
        };
      });
    }

    return readFileAsDataUrl(file).then(function (dataUrl) {
      if (dataUrl.length > MAX_PROOF_DATA_URL_LENGTH) {
        throw new Error("Please upload a smaller proof file. PDF uploads are more likely to fail when they are large.");
      }

      return {
        dataUrl: dataUrl,
        fileName: file.name || "proof-upload",
        mimeType: file.type || "application/octet-stream"
      };
    });
  }

  function postJson(url, payload, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var timeoutId;

      if (!normalize(url)) {
        reject(new Error("Add the Supabase endpoint before collecting applications."));
        return;
      }

      timeoutId = window.setTimeout(function () {
        reject(new Error("The request timed out. Please try again."));
      }, timeoutMs || SUBMISSION_TIMEOUT_MS);

      window.fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json().catch(function () {
            return {};
          }).then(function (data) {
            if (!response.ok) {
              throw new Error(data && data.message ? data.message : "The request could not be completed.");
            }

            return data;
          });
        })
        .then(resolve)
        .catch(function (error) {
          reject(error instanceof Error ? error : new Error("The request could not be completed."));
        })
        .finally(function () {
          window.clearTimeout(timeoutId);
        });
    });
  }

  function clearUnrelatedFormStatusFromUrl() {
    var currentUrl = new URL(window.location.href);
    var changed = false;

    ["customer_posted", "contact_posted", "form_type", "utf8"].forEach(function (key) {
      if (currentUrl.searchParams.has(key)) {
        currentUrl.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      window.history.replaceState({}, document.title, currentUrl.toString());
    }
  }

  function getPendingSubmission() {
    if (!pendingSubmissionState || !pendingSubmissionState.reference) {
      return null;
    }

    return pendingSubmissionState;
  }

  function setPendingSubmission(reference) {
    if (!reference) {
      return;
    }

    pendingSubmissionState = {
      reference: reference,
      startedAt: Date.now()
    };
  }

  function clearPendingSubmission() {
    pendingSubmissionState = null;
  }

  function isPendingSubmissionError(error) {
    return Boolean(error && error.code === "CLAIM_SUBMISSION_PENDING");
  }

  function setSubmitting(button, isSubmitting, config) {
    if (!button) {
      return;
    }

    button.disabled = isSubmitting;
    button.textContent = isSubmitting
      ? config.submittingLabel || "Submitting your application..."
      : config.submitLabel || "Submit My Free Perfume Application";
  }

  function setMessage(node, message, tone) {
    if (!node) {
      return;
    }

    node.textContent = message || "";
    node.classList.remove("is-error", "is-success", "is-info");

    if (tone) {
      node.classList.add(tone);
    }
  }

  function validatePasscodeLocally(code, config) {
    if (!normalize(code)) {
      return { ok: false, message: "Enter your access code first." };
    }

    return { ok: true, mode: "local", message: "Checking code..." };
  }

  function verifyPasscodeServerSide(code, config, referenceToken) {
    return postJson(config.claimVerifyUrl, {
      code: normalize(code),
      ref: normalize(referenceToken)
    }, 15000).then(function (result) {
      return result || { ok: false, message: "This code could not be verified." };
    });
  }

  function submitApplication(form, config, options) {
    var proofInput = form.querySelector("[data-claim-proof-file]");
    var proofFile = proofInput && proofInput.files ? proofInput.files[0] : null;
    var submitUrl = normalize(config.claimSubmitUrl || config.claimVerifyUrl);

    if (!submitUrl) {
      return Promise.reject(new Error("Add the Supabase submit URL before collecting applications."));
    }

    if (!proofFile) {
      return Promise.reject(new Error("Add a purchase screenshot before submitting."));
    }

    if (proofFile.size > 2 * 1024 * 1024) {
      return Promise.reject(new Error("Please upload a screenshot smaller than 2 MB."));
    }

    return buildProofPayload(proofFile).then(function (proofPayload) {
      var payload;
      var address = getFieldValue(form, "addressFormatted") || [
        getFieldValue(form, "streetAddress"),
        getFieldValue(form, "apartment"),
        getFieldValue(form, "city"),
        getFieldValue(form, "state"),
        getFieldValue(form, "zipCode"),
        getFieldValue(form, "country")
      ].filter(Boolean).join(", ");

      payload = {
        code: getFieldValue(form, "contact[Access Code]") || getFieldValue(form, "code"),
        ref: getFieldValue(form, "ref"),
        name: getFieldValue(form, "name"),
        phone: getFieldValue(form, "phone"),
        email: getFieldValue(form, "email"),
        address: address,
        streetAddress: getFieldValue(form, "streetAddress"),
        apartment: getFieldValue(form, "apartment"),
        city: getFieldValue(form, "city"),
        state: getFieldValue(form, "state"),
        zipCode: getFieldValue(form, "zipCode"),
        country: getFieldValue(form, "country"),
        addressVerified: getFieldValue(form, "addressVerified"),
        addressFormatted: getFieldValue(form, "addressFormatted"),
        addressPlaceId: getFieldValue(form, "addressPlaceId"),
        addressLatitude: getFieldValue(form, "addressLatitude"),
        addressLongitude: getFieldValue(form, "addressLongitude"),
        platform: getFieldValue(form, "platform"),
        orderNote: getFieldValue(form, "orderNote"),
        selectedGift: getFieldValue(form, "selectedGift"),
        selectedGiftVariantId: getFieldValue(form, "selectedGiftVariantId"),
        applicationType: getFieldValue(form, "applicationType"),
        campaignLabel: getFieldValue(form, "campaignLabel"),
        proofName: proofPayload.fileName || "proof-upload",
        proofType: proofPayload.mimeType || "application/octet-stream",
        proofDataUrl: proofPayload.dataUrl || ""
      };

      if (options && typeof options.onRequestSent === "function") {
        options.onRequestSent();
      }

      return postJson(submitUrl, payload, SUBMISSION_TIMEOUT_MS).then(function (serverResult) {
        if (!serverResult || serverResult.ok === false) {
          throw new Error((serverResult && serverResult.message) || "Application could not be submitted.");
        }

        return {
          ok: true,
          reference: (serverResult && (serverResult.reference || serverResult.ref)) || getFieldValue(form, "ref"),
          message: (serverResult && serverResult.message) || "Application submitted."
        };
      });
    });
  }

  function updateStepState(panels, indicators, nextStep) {
    Array.prototype.forEach.call(panels, function (panel, index) {
      panel.hidden = index !== nextStep;
    });

    Array.prototype.forEach.call(indicators, function (indicator, index) {
      indicator.classList.toggle("is-active", index === nextStep);
      indicator.classList.toggle("is-complete", index < nextStep);
    });
  }

  function validatePanel(panel) {
    var fields = panel.querySelectorAll("input, select, textarea");
    var addressVerifiedField = panel.querySelector("[data-claim-address-verified]");
    var streetAddressField = panel.querySelector("[data-claim-street-address]");
    var socialMessageNode = panel.querySelector("[data-claim-social-message]");
    var handledRadioNames = {};
    var index;
    var field;
    var group;

    if (panel.hasAttribute("data-claim-social-step") && panel.getAttribute("data-social-followed") !== "true") {
      setMessage(socialMessageNode, "Click Instagram, TikTok, or YouTube before choosing your free gift.", "is-error");
      return false;
    }

    if (streetAddressField) {
      streetAddressField.setCustomValidity("");
    }

    if (addressVerifiedField && addressVerifiedField.value !== "true") {
      if (streetAddressField) {
        streetAddressField.setCustomValidity("Please choose a Google-verified shipping address before continuing.");
        streetAddressField.reportValidity();
      }

      return false;
    }

    for (index = 0; index < fields.length; index += 1) {
      field = fields[index];

      if (field.disabled || field.type === "hidden" || !field.required) {
        continue;
      }

      if (field.type === "radio") {
        if (handledRadioNames[field.name]) {
          continue;
        }

        handledRadioNames[field.name] = true;
        group = panel.querySelectorAll('input[type="radio"][name="' + field.name.replace(/"/g, '\\"') + '"]');

        if (!Array.prototype.some.call(group, function (radio) { return radio.checked; })) {
          if (group[0]) {
            group[0].reportValidity();
          }
          return false;
        }

        continue;
      }

      if (!field.checkValidity()) {
        field.reportValidity();
        return false;
      }
    }

    return true;
  }

  function updatePreview(previewElements, sourceInput) {
    if (!previewElements.title || !previewElements.subtitle || !previewElements.summary || !previewElements.media) {
      return;
    }

    renderPreview(previewElements, getPreviewContent(sourceInput));
  }

  function getPreviewContent(sourceInput) {
    return {
      title: sourceInput.getAttribute("data-preview-title") || "Choose your scent",
      subtitle: sourceInput.getAttribute("data-preview-subtitle") || "",
      summary: sourceInput.getAttribute("data-preview-summary") || "",
      image: sourceInput.getAttribute("data-preview-image") || ""
    };
  }

  function renderPreview(previewElements, previewContent) {
    var title = previewContent.title;
    var subtitle = previewContent.subtitle;
    var summary = previewContent.summary;
    var image = previewContent.image;

    previewElements.title.textContent = title;
    previewElements.subtitle.textContent = subtitle || "Preview ready";
    previewElements.summary.textContent = summary || "No description available for this perfume yet.";

    if (image) {
      previewElements.media.innerHTML = '<img class="customer-free-perfume-claim__preview-image" src="' + image + '" alt="' + title.replace(/"/g, '&quot;') + '">';
    } else {
      previewElements.media.innerHTML = '<div class="customer-free-perfume-claim__preview-placeholder">No image uploaded for this perfume yet.</div>';
    }
  }

  function restorePreview(previewElements, defaults) {
    if (!previewElements.title || !previewElements.subtitle || !previewElements.summary || !previewElements.media) {
      return;
    }

    previewElements.title.textContent = defaults.title;
    previewElements.subtitle.textContent = defaults.subtitle;
    previewElements.summary.textContent = defaults.summary;
    previewElements.media.innerHTML = defaults.media;
  }

  function initClaimSection(root) {
    var form = root.querySelector("[data-customer-claim-form]");
    var config = parseConfig(root);
    var submitButton;
    var giftVariantField;
    var giftInputs;
    var giftChoices;
    var previewElements;
    var mobilePreviewElements;
    var mobilePreviewModal;
    var mobilePreviewDialog;
    var mobilePreviewCloseButtons;
    var mobilePreviewSelectButton;
    var panels;
    var nextButtons;
    var backButtons;
    var passcodeInput;
    var passcodeMessage;
    var passcodeStatusField;
    var passcodeReferenceField;
    var successPanel;
    var successReference;
    var successGift;
    var successCode;
    var successShipsTo;
    var socialStep;
    var socialLinks;
    var socialMessage;
    var wizard;
    var mobilePreviewSourceInput = null;
    var mobileViewport = window.matchMedia("(max-width: 749px)");
    var resetTimerId = null;
    var currentStep = 0;
    var passcodeVerified = false;
    var socialFollowPlatform = "";
    var verifyButton;
    var passcodeStepIndex;
    var addressFields;
    var socialFollowStorageKey;
    var draftStorageKey;

    if (!form) {
      return;
    }

    submitButton = root.querySelector("[data-customer-claim-submit]");
    verifyButton = root.querySelector("[data-claim-verify-button]");
    giftVariantField = root.querySelector("[data-claim-gift-variant]");
    giftInputs = root.querySelectorAll(".customer-free-perfume-option__radio");
    giftChoices = root.querySelectorAll("[data-gift-choice]");
    panels = root.querySelectorAll("[data-step-panel]");
    nextButtons = root.querySelectorAll("[data-step-next]");
    backButtons = root.querySelectorAll("[data-step-back]");
    passcodeInput = root.querySelector("[data-claim-passcode-input]");
    passcodeMessage = root.querySelector("[data-claim-passcode-message]");
    passcodeStatusField = root.querySelector("[data-claim-passcode-status]");
    passcodeReferenceField = root.querySelector("[data-claim-passcode-reference]");
    successPanel = root.querySelector("[data-claim-success]");
    successReference = root.querySelector("[data-claim-success-reference]");
    successGift = root.querySelector("[data-claim-success-gift]");
    successCode = root.querySelector("[data-claim-success-code]");
    successShipsTo = root.querySelector("[data-claim-success-ships-to]");
    socialStep = root.querySelector("[data-claim-social-step]");
    socialLinks = root.querySelectorAll("[data-claim-social-link]");
    socialMessage = root.querySelector("[data-claim-social-message]");
    wizard = root.querySelector("[data-claim-wizard]");
    mobilePreviewModal = root.querySelector("[data-claim-mobile-preview]");
    mobilePreviewDialog = root.querySelector("[data-claim-mobile-preview-dialog]");
    mobilePreviewCloseButtons = root.querySelectorAll("[data-claim-mobile-preview-close]");
    mobilePreviewSelectButton = root.querySelector("[data-claim-mobile-preview-select]");
    previewElements = {
      media: root.querySelector("[data-claim-gift-preview-media]"),
      title: root.querySelector("[data-claim-gift-preview-title]"),
      subtitle: root.querySelector("[data-claim-gift-preview-subtitle]"),
      summary: root.querySelector("[data-claim-gift-preview-summary]")
    };
    mobilePreviewElements = {
      media: root.querySelector("[data-claim-mobile-gift-preview-media]"),
      title: root.querySelector("[data-claim-mobile-gift-preview-title]"),
      subtitle: root.querySelector("[data-claim-mobile-gift-preview-subtitle]"),
      summary: root.querySelector("[data-claim-mobile-gift-preview-summary]")
    };
    var defaultPreview = {
      media: previewElements.media ? previewElements.media.innerHTML : "",
      title: previewElements.title ? previewElements.title.textContent : "",
      subtitle: previewElements.subtitle ? previewElements.subtitle.textContent : "",
      summary: previewElements.summary ? previewElements.summary.textContent : ""
    };
    passcodeStepIndex = passcodeInput && passcodeInput.closest("[data-step-panel]")
      ? Array.prototype.indexOf.call(panels, passcodeInput.closest("[data-step-panel]"))
      : 0;
    socialFollowStorageKey = "customer-free-perfume-claim-social-platform:" + form.id;
    draftStorageKey = "customer-free-perfume-claim-draft:" + form.id;

    function readBrowserStorage(storage, key) {
      try {
        return storage && typeof storage.getItem === "function"
          ? storage.getItem(key)
          : "";
      } catch (error) {
        return "";
      }
    }

    function writeBrowserStorage(storage, key, value) {
      try {
        if (!storage || typeof storage.setItem !== "function" || typeof storage.removeItem !== "function") {
          return;
        }

        if (value) {
          storage.setItem(key, value);
        } else {
          storage.removeItem(key);
        }
      } catch (error) {}
    }

    function readClaimDraft() {
      var rawDraft = readBrowserStorage(window.localStorage, draftStorageKey);
      var parsedDraft;

      if (!rawDraft) {
        return null;
      }

      try {
        parsedDraft = JSON.parse(rawDraft);
      } catch (error) {
        writeBrowserStorage(window.localStorage, draftStorageKey, "");
        return null;
      }

      if (!parsedDraft || typeof parsedDraft !== "object") {
        writeBrowserStorage(window.localStorage, draftStorageKey, "");
        return null;
      }

      if (!parsedDraft.updatedAt || Date.now() - Number(parsedDraft.updatedAt) > CLAIM_DRAFT_TTL_MS) {
        writeBrowserStorage(window.localStorage, draftStorageKey, "");
        return null;
      }

      return parsedDraft;
    }

    function clearClaimDraft() {
      writeBrowserStorage(window.localStorage, draftStorageKey, "");
    }

    function buildClaimDraft() {
      var fields = {};

      CLAIM_DRAFT_FIELD_NAMES.forEach(function (fieldName) {
        fields[fieldName] = getFieldValue(form, fieldName);
      });

      return {
        updatedAt: Date.now(),
        currentStep: currentStep,
        passcodeVerified: passcodeVerified,
        socialFollowPlatform: readSocialFollowPlatform(),
        fields: fields,
      };
    }

    function saveClaimDraft() {
      if (successPanel && !successPanel.hidden) {
        clearClaimDraft();
        return;
      }

      writeBrowserStorage(window.localStorage, draftStorageKey, JSON.stringify(buildClaimDraft()));
    }

    function setFieldValueByName(name, value) {
      var field = form.elements[name];
      var normalizedValue = value == null ? "" : String(value);

      if (!field) {
        return;
      }

      if (field instanceof RadioNodeList) {
        Array.prototype.forEach.call(field, function (option) {
          option.checked = option.value === normalizedValue;
        });
        return;
      }

      if (field.type === "checkbox") {
        field.checked = normalizedValue === "true" || normalizedValue === "1";
        return;
      }

      field.value = normalizedValue;
    }

    function restoreAddressFields(draftFields) {
      var restoredCity = normalize(draftFields.city);
      var restoredState = normalize(draftFields.state);
      var restoredCountry = normalize(draftFields.country) || "United States";

      if (!addressFields) {
        return;
      }

      if (addressFields.countrySelect) {
        setSelectValueByCandidates(addressFields.countrySelect, [restoredCountry, "United States", "United States of America"]);
      }

      if (addressFields.stateSelect && restoredState) {
        setSelectValueByCandidates(addressFields.stateSelect, [restoredState]);
      }

      if (normalize(draftFields.addressVerified) === "true" && restoredCity) {
        setSelectOptions(addressFields.citySelect, [restoredCity], "Select a verified city");
        addressFields.citySelect.value = restoredCity;
        addressFields.citySelect.disabled = false;

        if (addressFields.verifiedField) {
          addressFields.verifiedField.value = "true";
        }

        if (addressFields.formattedField) {
          addressFields.formattedField.value = normalize(draftFields.addressFormatted);
        }

        if (addressFields.placeIdField) {
          addressFields.placeIdField.value = normalize(draftFields.addressPlaceId);
        }

        if (addressFields.latitudeField) {
          addressFields.latitudeField.value = normalize(draftFields.addressLatitude);
        }

        if (addressFields.longitudeField) {
          addressFields.longitudeField.value = normalize(draftFields.addressLongitude);
        }

        if (addressFields.addressState) {
          addressFields.addressState.lastVerifiedSignature = buildAddressSignature(addressFields);
        }
      }
    }

    function restoreClaimDraft() {
      var draft = readClaimDraft();
      var draftFields;
      var restoredGiftInput;
      var restoredStep;
      var restoreMessage;

      if (!draft || !draft.fields) {
        return false;
      }

      draftFields = draft.fields;
      CLAIM_DRAFT_FIELD_NAMES.forEach(function (fieldName) {
        if (fieldName === "selectedGift" || fieldName === "city" || fieldName === "state" || fieldName === "country") {
          return;
        }

        setFieldValueByName(fieldName, draftFields[fieldName]);
      });

      restoreAddressFields(draftFields);

      restoredGiftInput = draftFields.selectedGift
        ? form.querySelector('input[name="selectedGift"][value="' + String(draftFields.selectedGift).replace(/"/g, '\\"') + '"]')
        : null;

      if (restoredGiftInput) {
        applyGiftSelection(restoredGiftInput);
      }

      if (draft.socialFollowPlatform) {
        writeSocialFollowPlatform(draft.socialFollowPlatform);
      }

      updateSocialFollowState();

      passcodeVerified = Boolean(draft.passcodeVerified && normalize(draftFields.ref));

      if (passcodeStatusField && passcodeVerified) {
        passcodeStatusField.value = normalize(draftFields.passcodeStatus) || "Verified";
      }

      restoredStep = passcodeVerified
        ? Math.max(passcodeStepIndex, Math.min(panels.length - 1, Number(draft.currentStep) || 0))
        : passcodeStepIndex;

      goToStep(restoredStep);

      restoreMessage = restoredStep >= panels.length - 1
        ? "We restored your in-progress application on this device. Please re-upload your screenshot before submitting."
        : "We restored your in-progress application on this device. Please continue where you left off.";

      if (normalize(draftFields.code)) {
        setMessage(passcodeMessage, restoreMessage, "is-info");
      }

      saveClaimDraft();
      return true;
    }

    function readSocialFollowPlatform() {
      socialFollowPlatform = readBrowserStorage(window.localStorage, socialFollowStorageKey)
        || readBrowserStorage(window.sessionStorage, socialFollowStorageKey)
        || socialFollowPlatform
        || "";

      return socialFollowPlatform;
    }

    function writeSocialFollowPlatform(platform) {
      socialFollowPlatform = platform || "";

      writeBrowserStorage(window.localStorage, socialFollowStorageKey, socialFollowPlatform);
      writeBrowserStorage(window.sessionStorage, socialFollowStorageKey, socialFollowPlatform);
    }

    function updateSocialFollowState() {
      var activePlatform = readSocialFollowPlatform();

      if (socialStep) {
        socialStep.setAttribute("data-social-followed", activePlatform ? "true" : "false");
      }

      Array.prototype.forEach.call(socialLinks, function (link) {
        link.classList.toggle("is-clicked", Boolean(activePlatform) && link.getAttribute("data-platform") === activePlatform);
      });

      if (socialMessage && activePlatform) {
        setMessage(socialMessage, "", "");
      }
    }

    function clearSocialFollowState() {
      writeSocialFollowPlatform("");

      if (socialMessage) {
        setMessage(socialMessage, "", "");
      }

      updateSocialFollowState();
      saveClaimDraft();
    }

    function markSocialFollow(platform) {
      writeSocialFollowPlatform(platform || "clicked");

      if (socialMessage) {
        setMessage(socialMessage, "", "");
      }

      updateSocialFollowState();
      saveClaimDraft();
    }

    function closeMobilePreview() {
      if (!mobilePreviewModal) {
        return;
      }

      mobilePreviewModal.hidden = true;
      mobilePreviewSourceInput = null;
    }

    function openMobilePreview(sourceInput) {
      if (!mobilePreviewModal || !mobileViewport.matches || !sourceInput) {
        return;
      }

      mobilePreviewSourceInput = sourceInput;
      updatePreview(mobilePreviewElements, sourceInput);
      mobilePreviewModal.hidden = false;

      if (mobilePreviewDialog) {
        mobilePreviewDialog.focus();
      }
    }

    function applyGiftSelection(sourceInput) {
      if (!sourceInput) {
        return;
      }

      sourceInput.checked = true;

      if (giftVariantField) {
        giftVariantField.value = sourceInput.getAttribute("data-variant-id") || "";
      }

      updatePreview(previewElements, sourceInput);
      sourceInput.dispatchEvent(new Event("change", { bubbles: true }));
      saveClaimDraft();
    }

    function goToStep(nextStep) {
      currentStep = nextStep;
      updateStepState(panels, [], currentStep);

      if (addressFields && typeof addressFields.refreshMap === "function") {
        addressFields.refreshMap();
      }

      saveClaimDraft();
    }

    function clearPasscodeVerification() {
      passcodeVerified = false;

      if (passcodeStatusField) {
        passcodeStatusField.value = "Pending";
      }

      if (passcodeReferenceField) {
        passcodeReferenceField.value = "";
      }

      saveClaimDraft();
    }

    function clearResetTimer() {
      if (resetTimerId) {
        window.clearTimeout(resetTimerId);
        resetTimerId = null;
      }
    }

    function applyPendingSubmissionState() {
      var pendingSubmission = getPendingSubmission();

      if (!pendingSubmission || !pendingSubmission.reference) {
        return false;
      }

      if (passcodeReferenceField) {
        passcodeReferenceField.value = pendingSubmission.reference;
      }

      if (passcodeStatusField) {
        passcodeStatusField.value = "Submission pending";
      }

      setMessage(passcodeMessage, "Your previous submission is still processing. Please wait and do not submit again yet.", "is-info");
      setSubmitting(submitButton, true, {
        submittingLabel: "Submission pending...",
        submitLabel: config.submitLabel
      });

      return true;
    }

    function releasePendingSubmissionState() {
      clearPendingSubmission();
      setSubmitting(submitButton, false, config);
    }

    function resetApplicationState() {
      clearResetTimer();
      form.reset();
      clearPasscodeVerification();
      setMessage(passcodeMessage, "", "");
      clearClaimDraft();

      if (giftVariantField) {
        giftVariantField.value = "";
      }

      if (successReference) {
        successReference.textContent = "";
      }

      if (successGift) {
        successGift.textContent = "Complimentary gift";
      }

      if (successCode) {
        successCode.textContent = "";
      }

      if (successShipsTo) {
        successShipsTo.textContent = "";
      }

      if (successPanel) {
        successPanel.hidden = true;
      }

      clearSocialFollowState();

      if (wizard) {
        wizard.hidden = false;
      }

      closeMobilePreview();
      restorePreview(previewElements, defaultPreview);
      goToStep(0);

      if (addressFields) {
        clearVerifiedAddress(addressFields, "", "", false);
      }

      if (!applyPendingSubmissionState()) {
        setSubmitting(submitButton, false, config);
      }
    }

    function buildSuccessShipToValue() {
      var name = getFieldValue(form, "name");
      var city = getFieldValue(form, "city");
      var state = getFieldValue(form, "state");
      var address = getFieldValue(form, "addressFormatted");
      var parts = [];

      if (name) {
        parts.push(name);
      }

      if (city && state) {
        parts.push(city + ", " + state);
      } else if (city || state) {
        parts.push(city || state);
      } else if (address) {
        parts.push(address);
      }

      return parts.join(", ");
    }

    function showSuccessAndAutoReset(reference) {
      clearResetTimer();
      releasePendingSubmissionState();
      clearClaimDraft();

      if (successReference) {
        successReference.textContent = reference || "";
      }

      if (successGift) {
        successGift.textContent = getFieldValue(form, "selectedGift") || "Complimentary gift";
      }

      if (successCode) {
        successCode.textContent = getFieldValue(form, "contact[Access Code]") || getFieldValue(form, "code") || "";
      }

      if (successShipsTo) {
        successShipsTo.textContent = buildSuccessShipToValue();
      }

      if (wizard) {
        wizard.hidden = true;
      }

      if (successPanel) {
        successPanel.hidden = false;
        successPanel.focus();
      }
    }

    if (passcodeInput) {
      passcodeInput.addEventListener("input", function () {
        if (getPendingSubmission()) {
          setMessage(passcodeMessage, "Your previous submission is still processing. Please wait and do not submit again yet.", "is-info");
          return;
        }

        clearPasscodeVerification();
        setMessage(passcodeMessage, "", "");
        saveClaimDraft();
      });
    }

    function handlePasscodeVerification() {
      var localResult;
      var activeReference;

      if (getPendingSubmission()) {
        setMessage(passcodeMessage, "Your previous submission is still processing. Please wait and do not submit again yet.", "is-info");
        return;
      }

      localResult = validatePasscodeLocally(getFieldValue(form, "contact[Access Code]") || getFieldValue(form, "code"), config);

      if (!localResult.ok) {
        clearPasscodeVerification();
        setMessage(passcodeMessage, localResult.message, "is-error");
        if (passcodeInput) {
          passcodeInput.reportValidity();
        }
        return;
      }

      if (passcodeReferenceField && !passcodeReferenceField.value) {
        passcodeReferenceField.value = buildReferenceToken();
      }

      activeReference = getFieldValue(form, "ref");

      setSubmitting(verifyButton, true, {
        submitLabel: "Verify code",
        submittingLabel: "Verifying..."
      });
      setMessage(passcodeMessage, "Checking your code...", "is-info");

      verifyPasscodeServerSide(getFieldValue(form, "code"), config, activeReference)
        .then(function (result) {
          if (!result || result.ok === false) {
            throw new Error((result && result.message) || "This code could not be verified.");
          }

          passcodeVerified = true;

          if (passcodeStatusField) {
            passcodeStatusField.value = "Verified";
          }

          if (passcodeReferenceField && (result.reference || result.ref)) {
            passcodeReferenceField.value = result.reference || result.ref;
          }

          setMessage(passcodeMessage, result.message || "Code accepted.", "is-success");
          goToStep(Math.min(panels.length - 1, passcodeStepIndex + 1));
          saveClaimDraft();
        })
        .catch(function (error) {
          clearPasscodeVerification();
          setMessage(passcodeMessage, error && error.message ? error.message : "This code could not be verified.", "is-error");
        })
        .finally(function () {
          setSubmitting(verifyButton, false, {
            submitLabel: "Verify code",
            submittingLabel: "Verifying..."
          });
        });
    }

    Array.prototype.forEach.call(giftInputs, function (input) {
      input.addEventListener("mouseenter", function () {
        updatePreview(previewElements, input);
      });

      input.addEventListener("focus", function () {
        updatePreview(previewElements, input);
      });

      input.addEventListener("change", function () {
        if (giftVariantField) {
          giftVariantField.value = input.getAttribute("data-variant-id") || "";
        }

        updatePreview(previewElements, input);
        saveClaimDraft();
      });
    });

    Array.prototype.forEach.call(giftChoices, function (choice) {
      choice.addEventListener("click", function (event) {
        var input = choice.querySelector(".customer-free-perfume-option__radio");

        if (!mobileViewport.matches || !input || input.disabled) {
          return;
        }

        event.preventDefault();
        openMobilePreview(input);
      });
    });

    Array.prototype.forEach.call(mobilePreviewCloseButtons, function (button) {
      button.addEventListener("click", function () {
        closeMobilePreview();
      });
    });

    Array.prototype.forEach.call(socialLinks, function (link) {
      link.addEventListener("click", function () {
        markSocialFollow(link.getAttribute("data-platform"));
      });
    });

    updateSocialFollowState();

    if (mobilePreviewDialog) {
      mobilePreviewDialog.addEventListener("keydown", function (event) {
        if (event.key === "Escape") {
          closeMobilePreview();
        }
      });
    }

    if (mobilePreviewSelectButton) {
      mobilePreviewSelectButton.addEventListener("click", function () {
        applyGiftSelection(mobilePreviewSourceInput);
        closeMobilePreview();
      });
    }

    Array.prototype.forEach.call(nextButtons, function (button) {
      button.addEventListener("click", function () {
        var panel = button.closest("[data-step-panel]");
        var nextStep = currentStep + 1;

        if (!panel || !validatePanel(panel)) {
          return;
        }

        goToStep(nextStep);
      });
    });

    Array.prototype.forEach.call(backButtons, function (button) {
      button.addEventListener("click", function () {
        goToStep(Math.max(0, currentStep - 1));
      });
    });

    if (verifyButton) {
      verifyButton.addEventListener("click", handlePasscodeVerification);
    }

    function handleFormSubmission(event) {
      var index;
      var activeReference;

      if (event) {
        event.preventDefault();
      }

      if (getPendingSubmission()) {
        setMessage(passcodeMessage, "Your previous submission is still processing. Please wait and do not submit again yet.", "is-info");
        setSubmitting(submitButton, true, {
          submittingLabel: "Submission pending...",
          submitLabel: config.submitLabel
        });
        return;
      }

      if (!passcodeVerified || !getFieldValue(form, "ref")) {
        goToStep(passcodeStepIndex);
        setMessage(passcodeMessage, "Verify your code before submitting the application.", "is-error");
        return;
      }

      if (passcodeStatusField) {
        passcodeStatusField.value = "Verified";
      }

      activeReference = getFieldValue(form, "ref");

      for (index = 0; index < panels.length; index += 1) {
        if (!validatePanel(panels[index])) {
          goToStep(index);
          return;
        }
      }

      setPendingSubmission(activeReference);
      setSubmitting(submitButton, true, {
        submittingLabel: "Preparing your screenshot...",
        submitLabel: config.submitLabel
      });

      submitApplication(form, config, {
        onRequestSent: function () {
          setPendingSubmission(activeReference);
          setMessage(passcodeMessage, "Request sent. Waiting for server response...", "is-info");
          setSubmitting(submitButton, true, {
            submittingLabel: "Request sent...",
            submitLabel: config.submitLabel
          });
        }
      })
        .then(function (result) {
          showSuccessAndAutoReset(result.reference || getFieldValue(form, "ref") || "");
        })
        .catch(function (error) {
          if (!isPendingSubmissionError(error)) {
            releasePendingSubmissionState();
          }

          setMessage(
            passcodeMessage,
            error && error.message ? error.message : "Application could not be submitted.",
            isPendingSubmissionError(error) ? "is-info" : "is-error"
          );
          goToStep(Math.max(passcodeStepIndex, panels.length - 1));
        })
        .finally(function () {
          if (!getPendingSubmission()) {
            setSubmitting(submitButton, false, config);
          }
        });
    }

    form.addEventListener("submit", handleFormSubmission);

    if (submitButton) {
      submitButton.addEventListener("click", handleFormSubmission);
    }

    addressFields = initAddressVerification(root, form, config);

    if (addressFields) {
      addressFields.refreshMap = function () {
        refreshAddressMap(addressFields, addressFields.addressState);
      };
    }

    Array.prototype.forEach.call(form.querySelectorAll("input, select, textarea"), function (field) {
      if (field.type === "file") {
        return;
      }

      field.addEventListener("input", saveClaimDraft);
      field.addEventListener("change", saveClaimDraft);
    });

    window.addEventListener("pagehide", saveClaimDraft);
    window.addEventListener("beforeunload", saveClaimDraft);

    if (!restoreClaimDraft()) {
      resetApplicationState();
    }
  }

  function init() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    clearUnrelatedFormStatusFromUrl();
    Array.prototype.forEach.call(roots, initClaimSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
